import {
  FilterTabEnum,
  JobCancellationStatusEnum,
  JobItem,
  JobWorkflowStatusEnum,
} from 'domains/workspaceDomain';
import { usePermissionFilterTabs, useRole } from 'hooks';
import { getJobCardActions, JobCardActionEnum } from '../list/jobCardActions';
import useJobCardInfo from './useJobCardInfo';

/** The actions this job card offers, before the handlers are attached. */
export const useJobCardActions = (data: JobItem): JobCardActionEnum[] => {
  const {
    onlyOneParticipant,
    hasCustomer,
    hasProxy,
    hasOperator,
    hasProducer,
    isInternalJob,
  } = useJobCardInfo(data);

  return getJobCardActions({
    workflowStatus: data.workflowStatus,
    onlyOneParticipant,
    hasCustomer,
    hasProxy,
    hasOperator,
    hasProducer,
    isInternalJob,
  });
};

/**
 * Whether this card may cancel the job -- the same answer the Actions menu reaches, so the Canva
 * card-level Cancel Job link appears exactly when the menu entry does and never offers a cancel
 * the menu would have withheld.
 */
export const useJobCanCancel = (data: JobItem): boolean => {
  const filterTabs = usePermissionFilterTabs(data);
  const actions = useJobCardActions(data);
  const { onlyOneParticipant, hasCustomer, hasProxy } = useJobCardInfo(data);

  const actionVisible =
    !(onlyOneParticipant && (hasCustomer || hasProxy)) ||
    data.workflowStatus !== JobWorkflowStatusEnum.COMPLETED;

  return (
    Boolean(filterTabs.tabs?.includes(FilterTabEnum.MORE_ACTIONS)) &&
    actionVisible &&
    actions.includes(JobCardActionEnum.CancelJob)
  );
};

/**
 * Where a cancel request is no longer the buyer's to make.
 *
 * Stated as what is closed rather than what is open, because which workflow statuses sit behind a
 * buyer tab is the server's call -- the workspace asks for a tab by name and gets back whatever it
 * decides belongs there. Buyer Proofing, for one, holds PO_SENT, PO_READ, PO_ACCEPT and RFM jobs
 * alongside the proof statuses. Listing the open ones meant a status nobody thought of silently
 * lost the button; listing the closed ones means it shows, which is the harmless direction.
 *
 * The two ends: nothing is ordered yet (Saved Quotes, and jobs still being configured), or it has
 * already shipped. Everything in between -- Need Artwork, Proofing, In Production -- can be asked
 * about, and a CSR decides what is still possible.
 */
const BUYER_CANCEL_REQUEST_CLOSED_STATUSES = [
  JobWorkflowStatusEnum.QUOTE,
  JobWorkflowStatusEnum.IN_CART,
  JobWorkflowStatusEnum.ORDERING,
  JobWorkflowStatusEnum.RFQ,
  JobWorkflowStatusEnum.SHIPPED,
  JobWorkflowStatusEnum.DELIVERED,
  JobWorkflowStatusEnum.INVOICING,
  JobWorkflowStatusEnum.COMPLETED,
  JobWorkflowStatusEnum.CANCELLED,
];

/**
 * Whether this card offers the buyer the cancel-request button (PRD-416).
 *
 * Deliberately shares nothing with useJobCanCancel but the placement on the card: that one gates
 * an actual cancellation by participant mix, this one gates a support request by role and status.
 * A buyer has no Actions menu at all, so there is no menu entry to stay in step with.
 *
 * Reads the job alone, never the workspace's status filter: that filter is one sticky value for
 * the whole workspace, so on the job detail page, in a project or in order history it still holds
 * whichever tab the buyer last visited and says nothing about the card in front of them.
 *
 * Withheld once a cancellation is already under way, so the buyer does not file a second ticket
 * for a job a CSR is already cancelling. Only the two states that say so hide it -- a job list
 * that omits the field should still show the button rather than silently lose it.
 */
export const useBuyerCanRequestCancel = (data: JobItem): boolean => {
  const { buyerRole } = useRole();

  return (
    buyerRole &&
    !BUYER_CANCEL_REQUEST_CLOSED_STATUSES.includes(data.workflowStatus) &&
    ![
      JobCancellationStatusEnum.IN_PROGRESS,
      JobCancellationStatusEnum.JOB_CANCELED,
    ].includes(data.cancellationStatus)
  );
};
